"use client";

import { useCallback, useRef } from "react";
import type {
  AgentType,
  Message,
  SendMessageOptions,
  Suggestion,
  TimeSaved,
} from "../types";
import { extractAgentPayload } from "../utils/chatbotUtils";
import { parseBlocks } from "../schemas/blocks";
import { useStructuredBlocksChart } from "./useStructuredBlocksChart";
import { useStructuredBlocksSuggestions } from "./useStructuredBlocksSuggestions";
import { UseSendMessageArgs } from "../types";
import { useTranslation } from "react-i18next";
function formatDateKey(d = new Date()) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Gera um ID único. Usa `crypto.randomUUID()` quando disponível (HTTPS /
 * secure context); cai para uma implementação baseada em `Math.random()`
 * compatível com HTTP local e ambientes de teste.
 */
function generateId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  // Fallback: UUID v4-like via Math.random
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}
import {
  EVENT_CATEGORIES,
  EVENTS,
  useActionsTrack,
} from "@/_old/features/analytics";
import { fetchWithTokenRefresh } from "@/core/utils/http/fetch-with-token-refresh";
import { useChatbot } from "../context/chatbot-context";
import { useBrunaWosChat } from "./useBrunaWosChat";
import { openBrunaStream, readBrunaTurn } from "../services/bruna-web-door";

interface StreamEvent {
  // Backend pode emitir um evento dedicado "agent_type" entre status e tokens,
  // carregando o agente escolhido pelo classificador.
  type: "status" | "token" | "response" | "done" | "error" | "agent_type";
  content?: string;
  message?: string;
  status?: string;
  node?: string;
  agent?: string;
  suggestions?: Suggestion[];
  time_saved?: TimeSaved;
  selected_agent?: string;
  run_id?: string;
  response_expandable?: { label: string; emails: string[] };
  response_added_list?: { label: string; emails: string[] };
  // Backend usa "strategy" (sem "e") mas o contrato documentado é "estrategy" — normalizamos abaixo.
  agent_type?: string | null;
  // Structured response blocks crus (validados via parseBlocks no `done`) — RFC T1+T2.
  blocks?: unknown;
  blocks_version?: number;
}

/**
 * Normaliza o valor de `agent_type` vindo do backend para o literal usado no front.
 * Backend manda "strategy"; o registry interno é "estrategy" (typo intencional do contrato).
 */
function normalizeAgentType(
  raw: string | undefined | null,
): AgentType | undefined {
  if (!raw) return undefined;
  const v = raw.toLowerCase();
  if (v === "operation" || v === "operations") return "operation";
  if (v === "strategy" || v === "estrategy") return "estrategy";
  if (v === "experience") return "experience";
  return undefined;
}

/**
 * Converte o literal interno do front para o valor esperado pelo backend ao
 * enviar `forced_agent_type`. O registry interno usa "estrategy" (typo do
 * contrato), mas a rota /api/agent/stream espera "strategy" — enviar o literal
 * interno faz o classificador rejeitar o agente de Estratégia.
 */
function toBackendAgentType(agent: AgentType): string {
  if (agent === "estrategy") return "strategy";
  return agent;
}

// Fallback messages when server is silent for too long
const FALLBACK_WAITING_MESSAGES = [
  "Isso pode levar alguns segundos",
  "Ainda estou processando",
  "Quase lá, só mais um momento",
];

// How long to wait before showing fallback message (when server is silent)
const SILENCE_THRESHOLD_MS = 5000;

type OnActionClick = (
  event: keyof typeof EVENTS,
  category: keyof typeof EVENT_CATEGORIES,
  data?: object,
) => void;

function trackFirstInteractionOncePerDay(
  companyId: string | number | null | undefined,
  userId: string | number | null | undefined,
  onActionClick: OnActionClick,
) {
  if (!companyId || !userId || !onActionClick) return;

  const key = `liaChatTracked:${companyId}:${userId}:${formatDateKey()}`;

  try {
    if (!localStorage.getItem(key)) {
      onActionClick("CHATBOT_SEND_MESSAGE", "CHATBOT");
      localStorage.setItem(key, "1");
    }
  } catch {
    console.error("Error tracking first interaction");
  }
}

/**
 * Hook to send messages with streaming SSE.
 * Shows the response being generated in real time.
 */
export function useSendMessageStream({
  inputValue,
  setInputValue,
  isLoading,
  setIsLoading,
  setMessages,
  appUserId,
  companyId,
  conversationId,
}: UseSendMessageArgs) {
  const [translate] = useTranslation(["core"]);
  const { onActionClick } = useActionsTrack();
  const { forcedAgent, setForcedAgent, setActiveAgent } = useChatbot();
  // ST-2: quando ligada, o `chart` block estruturado tem precedência sobre o gráfico
  // legado extraído do texto (evita duplicado durante a migração).
  const preferBlocksChart = useStructuredBlocksChart();
  // ST-3: quando ligada, os blocks chips/expandable_list têm precedência sobre os campos
  // legados (suggestions / response_expandable / response_added_list).
  const preferBlocksSuggestions = useStructuredBlocksSuggestions();
  // PED-14548: ligada, o chat fala com a Bruna pela porta web do manager.
  const brunaWosChat = useBrunaWosChat();
  const abortControllerRef = useRef<AbortController | null>(null);

  const sendMessage = useCallback(
    async (
      messageText?: string,
      displayText?: string,
      attachment?: { fileName: string; fileSize: number },
      options?: SendMessageOptions,
    ) => {
      const textToSend = messageText?.trim() || inputValue.trim();
      if (!textToSend || isLoading) return;

      // forced_agent_type vem do click no hub picker (options) ou de estado prévio (forcedAgent).
      // Consome-se na primeira mensagem para que o classificador volte a operar depois.
      const forcedAgentType =
        options?.forced_agent_type ?? forcedAgent ?? undefined;
      setActiveAgent(forcedAgentType ?? null);

      trackFirstInteractionOncePerDay(companyId, appUserId, onActionClick);

      const userMessageContent = displayText || textToSend;

      // IDs gerados com generateId() para evitar colisões quando duas
      // mensagens são criadas no mesmo milissegundo (suggestion click rápido, etc.)
      const userMessage: Message = {
        id: generateId(),
        content: userMessageContent,
        isUser: true,
        timestamp: new Date(),
        attachment: attachment ?? null,
      };

      const botMessageId = generateId();

      // Exibe imediatamente o turno neutro; a identidade é refinada pelo SSE.
      const initialBotMessage: Message = {
        id: botMessageId,
        content: translate("chatbot-thinking") as string,
        isUser: false,
        timestamp: new Date(),
        chart: null,
        feedback: null,
        isTyping: true, // Show typing indicator while processing
        agent_type: forcedAgentType ?? null,
      };

      setMessages((prev) => [...prev, userMessage, initialBotMessage]);
      if (!messageText) {
        setInputValue("");
      }
      setIsLoading(true);

      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      // ========================================
      // UNIFIED STATUS FEEDBACK SYSTEM
      // Shows immediate feedback + server status + fallback when silent
      // ========================================
      let isReceivingResponse = false;
      let lastEventTime = Date.now();
      let fallbackIndex = 0;
      let silenceCheckInterval: NodeJS.Timeout | null = null;

      // Helper to update bot message content
      const updateBotMessage = (content: string, extras?: Partial<Message>) => {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botMessageId ? { ...msg, content, ...extras } : msg,
          ),
        );
      };

      const updateBotMessageMetadata = (extras: Partial<Message>) => {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botMessageId ? { ...msg, ...extras } : msg,
          ),
        );
      };

      // Helper to set status message (replaces previous, single line - keeps typing indicator)
      const setStatusMessage = (message: string) => {
        updateBotMessage(message, { isTyping: true });
      };

      // Start interval to add fallback messages when server is silent
      const startSilenceCheck = () => {
        silenceCheckInterval = setInterval(() => {
          if (isReceivingResponse) {
            // Stop checking once we're receiving the actual response
            if (silenceCheckInterval) {
              clearInterval(silenceCheckInterval);
              silenceCheckInterval = null;
            }
            return;
          }

          const silenceTime = Date.now() - lastEventTime;

          // If server has been silent for too long, add a fallback message
          if (
            silenceTime >= SILENCE_THRESHOLD_MS &&
            fallbackIndex < FALLBACK_WAITING_MESSAGES.length
          ) {
            const fallbackMsg = FALLBACK_WAITING_MESSAGES[fallbackIndex];
            setStatusMessage(fallbackMsg);
            fallbackIndex++;
            lastEventTime = Date.now(); // Reset timer after showing fallback
          }
        }, 2000); // Check every 2 seconds
      };

      // Stop the silence check interval
      const stopSilenceCheck = () => {
        if (silenceCheckInterval) {
          clearInterval(silenceCheckInterval);
          silenceCheckInterval = null;
        }
      };

      // Start monitoring for server silence
      startSilenceCheck();

      try {
        if (brunaWosChat) {
          // A porta web da Bruna: a conversa é a do gestor na empresa do token, sem
          // conversation_id nem forced_agent_type; a resposta chega inteira no `done`.
          const response = await openBrunaStream(
            textToSend,
            abortControllerRef.current.signal,
          );
          if (!response.ok) {
            throw new Error(`HTTP error: ${response.status}`);
          }
          const turn = await readBrunaTurn(response, () => {
            lastEventTime = Date.now();
          });
          stopSilenceCheck();
          if (turn.failed) {
            updateBotMessage(translate("chatbot-error-occurred") as string, {
              isTyping: false,
            });
          } else {
            updateBotMessage(
              turn.answer ||
                (translate("chatbot-error-processing-request") as string),
              {
                chart: null,
                blocks: parseBlocks(turn.blocks),
                isTyping: false,
                agent_type: null,
              },
            );
          }
          return;
        }

        const baseUrl =
          import.meta.env.VITE_AGENT_BASE_URL ||
          import.meta.env.VITE_API_GATEWAY_URL ||
          "";
        // IMPORTANT: Use native fetch for SSE streaming (axios doesn't support it)
        const response = await fetchWithTokenRefresh(
          `${baseUrl}/api/agent/stream`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_id: String(appUserId ?? ""),
              company_id: String(companyId ?? ""),
              question: textToSend,
              // Continuar uma conversa existente (aberta do histórico): manda o id p/ o
              // backend anexar nela. Ausente/null → conversa nova (backend cria/usa Redis).
              ...(conversationId != null &&
                String(conversationId) !== "" && {
                  conversation_id: Number(conversationId),
                }),
              ...(forcedAgentType && {
                forced_agent_type: toBackendAgentType(forcedAgentType),
              }),
            }),
            signal: abortControllerRef.current.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error("Stream not available");
        }

        let accumulatedContent = "";
        let suggestions: Suggestion[] | undefined;
        let timeSaved: TimeSaved | undefined;
        let runId: string | undefined;
        let agentType: AgentType | undefined = forcedAgentType;
        // Buffer que acumula bytes entre chunks TCP para não perder eventos
        // cujo delimitador "\n\n" cai na fronteira de dois frames.
        let sseBuffer = "";

        try {
          // Process the stream
          while (true) {
            const result = await reader.read();

            if (!result) break;

            const { done, value } = result;
            if (done) break;

            // Acumula no buffer e divide somente nos delimitadores completos
            sseBuffer += decoder.decode(value, { stream: true });
            const parts = sseBuffer.split("\n\n");
            // O último elemento pode ser um fragmento incompleto — fica no buffer
            sseBuffer = parts.pop() ?? "";

            for (const line of parts) {
              if (line.startsWith("data: ")) {
                try {
                  const event: StreamEvent = JSON.parse(line.slice(6));

                  // Update last event time (server is active)
                  lastEventTime = Date.now();

                  // Captura agent_type assim que aparecer, permitindo UI mudar de cor
                  // antes do "done". Backend emite evento dedicado type="agent_type"
                  // ou inclui o campo em outros eventos.
                  const normalizedAgent = normalizeAgentType(event.agent_type);
                  if (normalizedAgent && normalizedAgent !== agentType) {
                    agentType = normalizedAgent;
                    setActiveAgent(normalizedAgent);
                    updateBotMessageMetadata({
                      agent_type: normalizedAgent,
                    });
                  }

                  switch (event.type) {
                    case "status":
                      if (event.message && !isReceivingResponse) {
                        setStatusMessage(event.message);
                      }
                      break;

                    case "token":
                      // Receive token by token
                      if (event.content) {
                        if (!isReceivingResponse) {
                          // First token - stop feedback and start showing response
                          isReceivingResponse = true;
                          stopSilenceCheck();
                          accumulatedContent = "";
                        }

                        accumulatedContent += event.content;
                        updateBotMessage(accumulatedContent, {
                          isTyping: false,
                        });
                      }
                      break;

                    case "response":
                      // Fallback: complete response (when there is no streaming of tokens)
                      if (event.content) {
                        isReceivingResponse = true;
                        stopSilenceCheck();
                        accumulatedContent = event.content;
                        updateBotMessage(accumulatedContent, {
                          isTyping: false,
                        });
                      }
                      break;

                    case "done":
                      suggestions = event.suggestions;
                      timeSaved = event.time_saved;
                      runId = event.run_id;

                      // Extract payload if the response contains visualization
                      let chart = null;
                      const payload = extractAgentPayload(accumulatedContent);
                      if (payload) {
                        accumulatedContent =
                          payload.reply || accumulatedContent;
                        chart = payload.visualization;
                        if (payload.suggestions && !suggestions) {
                          suggestions = payload.suggestions;
                        }
                      }

                      // RFC T1+T2: valida os blocks do evento `done` (discriminated union).
                      // Blocks inválidos/desconhecidos são descartados; o texto
                      // (accumulatedContent) segue como fallback.
                      const blocks = parseBlocks(event.blocks);

                      // ST-2: se veio um `chart` block estruturado e a flag está ligada, ele
                      // é a fonte de verdade do gráfico — suprime o chart legado extraído do
                      // texto para não renderizar dois gráficos iguais. Atrás de flag até o
                      // corte definitivo do `extractAgentPayload` (Fase 4/ST-8).
                      if (
                        preferBlocksChart &&
                        blocks.some((b) => b.type === "chart")
                      ) {
                        chart = null;
                      }

                      // ST-3: quando a flag está ligada e chegaram os blocks equivalentes,
                      // suprime os campos legados para não renderizar duplicado — chips
                      // substitui `suggestions`; expandable_list substitui os cards de
                      // e-mail (response_expandable/response_added_list). RFC §7 Fase 2.
                      let responseExpandable =
                        event.response_expandable ?? null;
                      let responseAddedList = event.response_added_list ?? null;
                      if (preferBlocksSuggestions) {
                        if (blocks.some((b) => b.type === "chips")) {
                          suggestions = undefined;
                        }
                        if (blocks.some((b) => b.type === "expandable_list")) {
                          responseExpandable = null;
                          responseAddedList = null;
                        }
                      }

                      // Update final message with all metadata (incl. response_expandable e response_added_list)
                      updateBotMessage(accumulatedContent, {
                        chart,
                        blocks,
                        suggestions,
                        time_saved: timeSaved,
                        run_id: runId,
                        response_expandable: responseExpandable,
                        response_added_list: responseAddedList,
                        isTyping: false,
                        agent_type: agentType ?? null,
                      });
                      break;

                    case "error":
                      stopSilenceCheck();
                      updateBotMessage(
                        event.message ||
                          (translate("chatbot-error-occurred") as string),
                        { isTyping: false },
                      );
                      break;
                  }
                } catch (e) {
                  console.error("Failed to parse SSE event:", e, line);
                }
              }
            }
          }
        } finally {
          stopSilenceCheck();
        }

        // If we didn't receive any content, show error
        if (!accumulatedContent) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === botMessageId
                ? {
                    ...msg,
                    content: translate(
                      "chatbot-error-processing-request",
                    ) as string,
                    isTyping: false,
                  }
                : msg,
            ),
          );
        }
      } catch (error: any) {
        stopSilenceCheck();

        const isAborted =
          error?.name === "AbortError" ||
          error?.message?.includes("aborted") ||
          error?.message?.includes("canceled");

        if (!isAborted) {
          let errorMessage = translate("chatbot-error-occurred") as string;

          if (error?.message?.includes("timeout")) {
            errorMessage =
              (translate("chatbot-error-timeout") as string) ||
              "A requisição demorou muito para responder. Por favor, tente novamente.";
          } else if (
            error?.message?.includes("Network") ||
            error?.message?.includes("Failed to fetch")
          ) {
            errorMessage =
              "Não foi possível conectar. Verifique sua conexão e tente novamente.";
          }

          // Update message with error instead of leaving empty
          updateBotMessage(errorMessage, { isTyping: false });
        } else {
          // If aborted, remove the empty bot message
          setMessages((prev) => prev.filter((msg) => msg.id !== botMessageId));
        }
      } finally {
        setIsLoading(false);
        abortControllerRef.current = null;
        // Consome forcedAgent após envio — próxima mensagem livre volta a ser classificada.
        if (forcedAgent) setForcedAgent(null);
      }
    },
    [
      inputValue,
      isLoading,
      setIsLoading,
      setInputValue,
      setMessages,
      appUserId,
      companyId,
      conversationId,
      translate,
      onActionClick,
      forcedAgent,
      setForcedAgent,
      setActiveAgent,
      brunaWosChat,
    ],
  );

  // Function to cancel the ongoing request
  const cancelRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsLoading(false);
    }
  }, [setIsLoading]);

  return { sendMessage, cancelRequest };
}
